import React, { useCallback, useEffect, useRef, useState } from 'react'
import { LoadingModal } from './LoadingModal'
import { useReactToPrint } from 'react-to-print'

type RenderProps = {
  onProgress: (progress: number) => void
  onReadyToPrint: () => void
}

type RenderFunc = (props: RenderProps) => React.ReactNode

type AsyncPrintResult = {
  print: (render: RenderFunc) => void
  renderPrintComponent: () => React.ReactNode
}

export const useAsyncPrint = (): AsyncPrintResult => {
  const [render, setRender] = useState<RenderFunc | null>(null)
  const [readyToPrint, setReadyToPrint] = useState(false)
  const [loadingProgress, setLoadingProgress] = useState<number | undefined>(undefined)
  const printComponentRef = useRef(null)
  const handlePrint = useReactToPrint({
    content: () => printComponentRef.current,
  })

  useEffect(() => {
    if (readyToPrint && handlePrint) {
      handlePrint()
      setRender(null)
      setReadyToPrint(false)
      setLoadingProgress(undefined)
    }
  }, [readyToPrint, handlePrint])

  const handleReadyToPrint = useCallback(() => {
    setReadyToPrint(true)
  }, [setReadyToPrint])
  const handleProgress = useCallback(
    (progress: number) => {
      setLoadingProgress(progress)
    },
    [setLoadingProgress]
  )

  const renderPrintComponent = useCallback(() => {
    if (!render) {
      return null
    }
    return (
      <>
        {loadingProgress !== undefined && (
          <LoadingModal
            value={loadingProgress * 100}
            open={loadingProgress !== undefined}
            onCancel={() => {
              setRender(null)
              setLoadingProgress(undefined)
            }}
          />
        )}
        <div style={{ display: 'none' }}>
          <div ref={printComponentRef}>
            {render({ onReadyToPrint: handleReadyToPrint, onProgress: handleProgress })}
          </div>
        </div>
      </>
    )
  }, [handleProgress, handleReadyToPrint, printComponentRef, render, loadingProgress])

  return {
    print: (render: RenderFunc) => {
      setRender(() => render)
      setLoadingProgress(0)
    },
    renderPrintComponent,
  }
}
